import { gql } from '@apollo/client'
import { parseSystemColumnFieldId } from '@doss/common-all'
import { Badge, Stack } from '@mui/material'
import {
  GridColumnVisibilityModel,
  GridSortModel,
  useGridApiContext,
} from '@mui/x-data-grid-premium'
import dayjs from 'dayjs'
import { forEach, get, includes, unionBy } from 'lodash'
import { SyntheticEvent, useMemo, useState } from 'react'
import { FiFilter, FiPlus } from 'react-icons/fi'
import { TbArrowsSort } from 'react-icons/tb'
import {
  CreateTableTabInput,
  GetDetailedTableDocument,
  GetDetailedTableQuery,
  SystemGeneratedTableColumnType,
  TableColumnFilterInput,
  useCreateTableTabMutation,
  useUpdateTableColumnFiltersMutation,
  useUpdateTableTabColumnsMutation,
  useUpdateTableTabSortsMutation,
} from '~/common/generated/apollo-operations'
import { useDossSignals } from '~/common/hooks/useDossSignals'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import {
  DossGridFilterModel,
  createUpdateTableColumnFiltersInput,
  filterOperatorToBooleanOperator,
  generateFilterInput,
  generateSystemColumnFilterInput,
} from '~/components/tables/filters'
import { createUpdateTableTabSortsInput } from '~/components/tables/sorts'
import {
  createUpdateTableTabColumnsInput,
  getGridColumnVisibilityModel,
} from '~/components/tables/table-column'
import {
  DetailedTableColumnWithTable,
  DetailedTableTab,
  TableType,
} from '~/components/tables/types'
import { Button } from '~/components/ui/atoms/button/Button'
import { LoadingMenuButton } from '~/components/ui/molecules/loading-menu-button/LoadingMenuButton'
import { useTypedTranslation } from '../../../common/hooks/useTypedTranslation'
import {
  getHasChangedFilters,
  getHasChangedSort,
  getHasChangedVisibleColumns,
} from '../../../common/utils/table'
import { useTableDetails } from '../../tables/TableDetailsProvider'
import { useDossSnackbar } from '../DossSnackbar'
import { TableSource } from '../DossTable'
import { SaveNewTableTabDialog } from './SaveNewTableTabDialog'
import { TableMenuOptions, TableMenuTypes, useTableMenuContext } from './TableMenus'

interface TableTabButtonsProps {
  selectedTab: DetailedTableTab
  handleClose: (event: Event | SyntheticEvent) => void
  filterModel: DossGridFilterModel
  sortModel: GridSortModel
  tableColumns: DetailedTableColumnWithTable[]
  columnVisibilityModel: GridColumnVisibilityModel
  savedColumnVisibilityModel?: GridColumnVisibilityModel
  onTableTabColumnsSaved?: (visibilityModel: GridColumnVisibilityModel) => void
  tableId: string
  tableName: string
  resetSelectedTab: () => void
  setOrderedTablesAfterTabCreation: (tableTab: DetailedTableTab) => void
  tableType: TableType
  readOnly: boolean
  tableSource: TableSource
}

/**
 * Function for rendering the sort and filter information for current tab
 * and the buttons to save or save as new view if the current tab has been
 * updated
 */
export const TableTabButtons = ({
  selectedTab,
  handleClose,
  filterModel,
  sortModel,
  columnVisibilityModel,
  savedColumnVisibilityModel,
  onTableTabColumnsSaved,
  tableColumns,
  tableId,
  resetSelectedTab,
  tableName,
  setOrderedTablesAfterTabCreation,
  tableType,
  readOnly,
  tableSource,
}: TableTabButtonsProps) => {
  const { hideToolbarButtons } = useTableDetails()
  const snackbar = useDossSnackbar()
  const { openMenu } = useTableMenuContext()
  const { pivotTableDrilldownChartName } = useDossSignals()
  const apiRef = useGridApiContext()
  const [loading, setLoading] = useState(false)
  const [pendingSaveAsNewView, setPendingSaveAsNewView] = useState<{
    defaultName: string
    input: Omit<CreateTableTabInput, 'name'>
  } | null>(null)
  const { t } = useTypedTranslation()
  const { permissionsManager } = useGlobalContext()

  const [updateTableTabColumns] = useUpdateTableTabColumnsMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }

      const normalizedId = cache.identify(selectedTab)
      const tableTabColumnsFragment = gql`
        fragment TableTabColumnsFragment on TableTab {
          id
          columns {
            id
            order
            isFrozen
            systemGeneratedColumnType
            tableColumn {
              id
              name
              order
              type
              isNullable
              enumOptions
              lookupColumn {
                id
                name
                table {
                  id
                  name
                }
              }
            }
          }
        }
      `
      const tableTab: DetailedTableTab | null = cache.readFragment({
        id: normalizedId,
        fragment: tableTabColumnsFragment,
      })

      if (tableTab) {
        const newColumns = data.updateTableTabColumns
        const columnsInput = createUpdateTableTabColumnsInput(
          selectedTab,
          columnVisibilityModel,
          tableColumns,
          tableId,
        )
        const existingColumns = tableTab.columns.filter((column) => {
          if (includes(columnsInput.tabColumnIdsToDelete, column.id)) {
            return false
          }
          return true
        })
        cache.writeFragment({
          id: normalizedId,
          fragment: tableTabColumnsFragment,
          data: { ...tableTab, columns: [...existingColumns, ...newColumns] },
        })
      }
    },
  })
  const [updateTableColumnFilters] = useUpdateTableColumnFiltersMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      const normalizedId = cache.identify(selectedTab)
      const tableTabsFiltersFragment = gql`
        fragment TableColumnFiltersFragment on TableTab {
          id
          booleanOperator
          filters {
            tableColumn {
              id
            }
            id
            operator
            operand
            systemGeneratedColumnType
          }
        }
      `
      const tableTab: DetailedTableTab | null = cache.readFragment({
        id: normalizedId,
        fragment: tableTabsFiltersFragment,
      })

      if (tableTab) {
        const newFilters = data.updateTableColumnFilters
        const filtersInput = createUpdateTableColumnFiltersInput(
          selectedTab,
          filterModel,
          tableColumns,
          filterModel.logicOperator,
        )
        const existingFilters = tableTab.filters.filter((filter) => {
          // Remove filters that were deleted
          return !includes(filtersInput.filtersToDelete, filter.id)
        })
        // Remove duplicates
        const updatedFilters = unionBy(newFilters, existingFilters, 'id')
        cache.writeFragment({
          id: normalizedId,
          fragment: tableTabsFiltersFragment,
          data: {
            ...tableTab,
            booleanOperator: filterOperatorToBooleanOperator(filterModel.logicOperator),
            filters: updatedFilters,
          },
        })
      }
    },
  })
  const [updateTableTabSorts] = useUpdateTableTabSortsMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      const normalizedId = cache.identify(selectedTab)
      const tableTabSortsFragment = gql`
        fragment TableTabSortsFragment on TableTab {
          id
          sorts {
            id
            direction
            tableColumn {
              id
            }
            systemGeneratedColumnType
          }
        }
      `
      const tableTab: DetailedTableTab | null = cache.readFragment({
        id: normalizedId,
        fragment: tableTabSortsFragment,
      })

      if (tableTab) {
        const newSorts = data.updateTableTabSorts
        cache.writeFragment({
          id: normalizedId,
          fragment: tableTabSortsFragment,
          data: { ...tableTab, sorts: newSorts },
        })
      }
    },
  })
  const [createTableTab] = useCreateTableTabMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      const queryData: GetDetailedTableQuery | null = cache.readQuery({
        query: GetDetailedTableDocument,
        variables: { tableId },
      })
      // Update the Apollo Cache with the new table tab
      if (queryData) {
        // Check if the tab already exists in the cache
        const tabExists = queryData.table.tableTabs.some(
          (existingTab) => existingTab.id === data.createTableTab.id,
        )

        if (!tabExists) {
          cache.writeQuery({
            query: GetDetailedTableDocument,
            variables: { tableId },
            data: {
              table: {
                ...queryData.table,
                tableTabs: [...queryData.table.tableTabs, data.createTableTab],
              },
            },
          })
        }
      }
    },
    onCompleted({ createTableTab }) {
      // If exceeding tabCountLimit, set table tab at last visible index on top of table
      // Otherwise, set table tab at last index
      setOrderedTablesAfterTabCreation(createTableTab)
    },
  })

  const tabVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
    // Only use the table columns that are on this table for the tab visibility model.
    // Pass empty object so system column visibility reflects only what's saved on the tab,
    // enabling change detection when the user toggles system columns.
    const thisTableColumns = tableColumns.filter((tableColumn) => tableColumn.table.id === tableId)
    if (!thisTableColumns.length) {
      return {}
    }
    return getGridColumnVisibilityModel(tableType, {}, thisTableColumns, selectedTab.columns)
  }, [selectedTab.columns, tableColumns, tableId, tableType])

  const handleResetClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    resetSelectedTab()
  }

  const handleOpenMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    openMenu(TableMenuTypes.TABLE, TableMenuOptions.FILTER, undefined, apiRef)
    handleClose(event)
  }

  const handleOpenSortMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation()
    openMenu(TableMenuTypes.TABLE, TableMenuOptions.SORT, undefined, apiRef)
    handleClose(event)
  }

  const handleSaveAsNewViewClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation()

    // Create column inputs for visible table columns and visible system-generated columns
    const columnInputs = []
    let order = 0
    const visibleTableColumns = tableColumns
      .filter((tableColumn) => tableColumn.table.id === tableId)
      .filter((tableColumn) => get(columnVisibilityModel, tableColumn.id, false))
      .sort((a, b) => a.order - b.order)
    for (const tableColumn of visibleTableColumns) {
      columnInputs.push({ tableColumnId: tableColumn.id, order: order++ })
    }
    for (const systemType of Object.values(SystemGeneratedTableColumnType)) {
      if (get(columnVisibilityModel, systemType, false)) {
        columnInputs.push({ systemGeneratedColumnType: systemType, order: order++ })
      }
    }

    // Create sort inputs for the current sorts applied
    const sortsInput = createUpdateTableTabSortsInput(sortModel)

    // Create filters inputs for the current filters applied (ignore for now per request)
    const filtersInput: TableColumnFilterInput[] = []
    forEach(filterModel.items, (filterItem) => {
      const tableColumn = tableColumns.find((tableColumn) => tableColumn.id === filterItem.field)
      if (tableColumn) {
        filtersInput.push(generateFilterInput(tableColumn, filterItem))
      } else {
        // If the table column ID is a system generated column, add it to the filters input
        const systemGeneratedColumnType = parseSystemColumnFieldId(filterItem.field)
        if (systemGeneratedColumnType) {
          filtersInput.push(
            generateSystemColumnFilterInput(
              systemGeneratedColumnType as SystemGeneratedTableColumnType,
              filterItem,
            ),
          )
        }
      }
      const systemGeneratedColumnType = parseSystemColumnFieldId(filterItem.field)
      if (systemGeneratedColumnType) {
        filtersInput.push(
          generateSystemColumnFilterInput(
            systemGeneratedColumnType as SystemGeneratedTableColumnType,
            filterItem,
          ),
        )
      }
    })

    const defaultName =
      tableSource === TableSource.PIVOT_TABLE_DRILLDOWN
        ? `${pivotTableDrilldownChartName} ${t('pivot_table.drilldown')}-${dayjs().format('M/D/YY h:mm A')}`
        : `${tableName}-${dayjs().format('M/D/YY h:mm A')}`

    // Open the naming dialog with the current filters, sorts, columns, and boolean operator;
    // the actual createTableTab mutation fires once the user confirms the (possibly edited) name.
    setPendingSaveAsNewView({
      defaultName,
      input: {
        tableId,
        booleanOperator: filterOperatorToBooleanOperator(filterModel.logicOperator),
        columns: columnInputs,
        sorts: sortsInput,
        filters: filtersInput,
      },
    })
  }

  const handleConfirmSaveAsNewView = async (name: string) => {
    if (!pendingSaveAsNewView) {
      return
    }

    await createTableTab({
      variables: { input: { ...pendingSaveAsNewView.input, name } },
    })
      .then(({ data }) => {
        if (!data) {
          snackbar.showError(t('table_tabs.error_creating_new_view'))
          return
        }
        snackbar.showSuccess(
          t('table_tabs.created_new_view', { viewName: data.createTableTab.name }),
        )
        setPendingSaveAsNewView(null)
      })
      .catch(() => {
        snackbar.showError(t('table_tabs.error_creating_new_view'))
      })
  }

  const hasChangedSort = getHasChangedSort(selectedTab, sortModel)
  const hasChangedFilters = getHasChangedFilters(
    tableColumns,
    selectedTab,
    filterModel,
    tableId,
    permissionsManager,
  )
  const baselineVisibilityModel = savedColumnVisibilityModel ?? tabVisibilityModel
  const hasChangedVisibleColumns = getHasChangedVisibleColumns(
    baselineVisibilityModel,
    columnVisibilityModel,
  )

  const handleSaveUpdatesClick = async () => {
    const promises = []
    setLoading(true)
    if (hasChangedVisibleColumns) {
      const columnsInput = createUpdateTableTabColumnsInput(
        selectedTab,
        columnVisibilityModel,
        tableColumns,
        tableId,
      )

      // Normalize so each column has exactly one of tableColumnId or systemGeneratedColumnType for the API
      const input = {
        tableTabId: columnsInput.tableTabId,
        tabColumnIdsToDelete: columnsInput.tabColumnIdsToDelete,
        columnsToCreate: columnsInput.columnsToCreate
          .filter(
            (
              col,
            ): col is
              | (typeof col & { tableColumnId: string })
              | (typeof col & {
                  systemGeneratedColumnType: NonNullable<typeof col.systemGeneratedColumnType>
                }) => col.tableColumnId != null || col.systemGeneratedColumnType != null,
          )
          .map((col) =>
            col.tableColumnId != null
              ? { tableColumnId: col.tableColumnId, order: col.order }
              : { systemGeneratedColumnType: col.systemGeneratedColumnType, order: col.order },
          ),
      }
      promises.push(
        updateTableTabColumns({
          variables: { input },
          refetchQueries: [
            {
              query: GetDetailedTableDocument,
              variables: { tableId },
            },
          ],
        }),
      )
    }

    if (hasChangedSort) {
      const sortsInput = createUpdateTableTabSortsInput(sortModel)

      promises.push(
        updateTableTabSorts({
          variables: {
            input: {
              tableTabId: selectedTab.id,
              sortsToCreate: sortsInput,
            },
          },
          refetchQueries: [
            {
              query: GetDetailedTableDocument,
              variables: { tableId },
            },
          ],
        }),
      )
    }

    if (hasChangedFilters) {
      const filtersInput = createUpdateTableColumnFiltersInput(
        selectedTab,
        filterModel,
        tableColumns,
        filterModel.logicOperator,
      )

      promises.push(
        updateTableColumnFilters({
          variables: {
            input: filtersInput,
          },
          refetchQueries: [
            {
              query: GetDetailedTableDocument,
              variables: { tableId },
            },
          ],
        }),
      )
    }

    // Handle form submission: capture mutation results and apply saved state from them
    // so dirty state clears without relying on cache timing (avoids flicker).
    await Promise.all(promises)
      .then(() => {
        if (hasChangedVisibleColumns) {
          onTableTabColumnsSaved?.(columnVisibilityModel)
        }
        snackbar.showSuccess(t('table_tabs.saved_view'))
      })
      .catch(() => {
        snackbar.showError(t('table_tabs.could_not_save_view'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  let filterText
  if (filterModel.items.length === 0) {
    filterText = t('table_tabs.filter')
  } else if (filterModel.items.length === 1) {
    filterText = t('table_tabs.filter_text_singular', { count: filterModel.items.length })
  } else {
    filterText = t('table_tabs.filter_text_plural', { count: filterModel.items.length })
  }

  let sortText
  if (sortModel.length === 0) {
    sortText = t('table_tabs.sort')
  } else if (sortModel.length === 1) {
    sortText = t('table_tabs.sort_text_singular', { count: sortModel.length })
  } else {
    sortText = t('table_tabs.sort_text_plural', { count: sortModel.length })
  }

  const saveOptions =
    tableType === TableType.TABLE
      ? [{ label: t('table_tabs.save_as_new_view'), onClick: handleSaveAsNewViewClick }]
      : []

  const showSave = !readOnly && (hasChangedSort || hasChangedFilters || hasChangedVisibleColumns)

  const canConfigureTableTabs = permissionsManager.canConfigureTableTabs(tableId)

  if (hideToolbarButtons) {
    return null
  }

  return (
    <>
      <Stack direction="row" spacing={1}>
        {/** Show basic save button when on the pivot table drilldown view */}
        {tableSource === TableSource.PIVOT_TABLE_DRILLDOWN && canConfigureTableTabs && (
          <Button
            onClick={handleSaveAsNewViewClick}
            size="small"
            startIcon={<FiPlus />}
            variant={'true-ghost'}
          >
            {t('pivot_table.save_to_view')}
          </Button>
        )}
        <Badge
          color="primary"
          invisible={!hasChangedSort && tableSource !== TableSource.PIVOT_TABLE_DRILLDOWN}
          variant="dot"
        >
          <Button
            onClick={handleOpenSortMenuClick}
            size="small"
            startIcon={<TbArrowsSort />}
            variant={hasChangedSort ? 'tertiary' : 'ghost'}
          >
            {sortText}
          </Button>
        </Badge>
        <Badge
          color="primary"
          invisible={!hasChangedFilters && tableSource !== TableSource.PIVOT_TABLE_DRILLDOWN}
          variant="dot"
        >
          <Button
            onClick={handleOpenMenuClick}
            size="small"
            startIcon={<FiFilter />}
            variant={hasChangedFilters ? 'tertiary' : 'ghost'}
          >
            {filterText}
          </Button>
        </Badge>
        {showSave && (
          <>
            <Button onClick={handleResetClick} size="small" variant={'ghost'}>
              {t('table_tabs.reset')}
            </Button>
            <LoadingMenuButton
              loading={loading}
              onLeftButtonClick={(event) => {
                event.stopPropagation()
                event.preventDefault()
                handleSaveUpdatesClick()
              }}
              options={saveOptions}
              showChevron={true}
              size={'small'}
              text={t('table_tabs.save')}
              variant={'secondary'}
            />
          </>
        )}
      </Stack>
      {pendingSaveAsNewView && (
        <SaveNewTableTabDialog
          defaultName={pendingSaveAsNewView.defaultName}
          onClose={() => setPendingSaveAsNewView(null)}
          onConfirm={handleConfirmSaveAsNewView}
          tableName={tableName}
        />
      )}
    </>
  )
}
